/**
 * UIStylePreview — shows all 4 proposed UI styles side-by-side
 * Navigate to /ui-preview (no authentication required)
 */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaYoutube, FaTiktok, FaInstagram, FaXTwitter, FaTwitch, FaFacebook,
} from 'react-icons/fa6';
import {
  EyeIcon, HeartIcon, ChatBubbleLeftIcon, CurrencyDollarIcon,
  ShieldCheckIcon, ArrowUpIcon, ArrowRightIcon,
} from '@heroicons/react/24/outline';

// ── Mock data ─────────────────────────────────────────────────────────────────

const PLATFORMS = [
  { key: 'YOUTUBE',   label: 'YouTube',    Icon: FaYoutube,   brand: '#FF0000', tw: 'text-red-500',    bg: 'bg-red-50 dark:bg-red-900/20',    score: 87 },
  { key: 'TIKTOK',    label: 'TikTok',     Icon: FaTiktok,    brand: '#010101', tw: 'text-gray-800',   bg: 'bg-gray-100 dark:bg-gray-800',    score: 72 },
  { key: 'INSTAGRAM', label: 'Instagram',  Icon: FaInstagram, brand: '#E1306C', tw: 'text-pink-500',   bg: 'bg-pink-50 dark:bg-pink-900/20',  score: 91 },
  { key: 'TWITTER',   label: 'X (Twitter)',Icon: FaXTwitter,  brand: '#000000', tw: 'text-gray-900',   bg: 'bg-gray-100 dark:bg-gray-800',    score: 68 },
  { key: 'TWITCH',    label: 'Twitch',     Icon: FaTwitch,    brand: '#9147FF', tw: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20', score: 0 },
  { key: 'FACEBOOK',  label: 'Facebook',   Icon: FaFacebook,  brand: '#1877F2', tw: 'text-blue-500',   bg: 'bg-blue-50 dark:bg-blue-900/20',  score: 0 },
];

const STATS = [
  { label: 'Total Views',   value: 2_410_000, display: '2.41M',  change: 14, Icon: EyeIcon,             color: '#0ea5e9' },
  { label: 'Total Likes',   value: 89_400,    display: '89.4K',  change: 8,  Icon: HeartIcon,           color: '#ef4444' },
  { label: 'Comments',      value: 12_800,    display: '12.8K',  change: 21, Icon: ChatBubbleLeftIcon,  color: '#10b981' },
  { label: 'Revenue',       value: 12_400,    display: '$12.4K', change: 6,  Icon: CurrencyDollarIcon,  color: '#f59e0b' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Animated counter — counts from 0 to `target` over `duration` ms once `active` is true */
function useCounter(target, duration = 1200, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setCount(Math.floor(easeOut(progress) * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, active]);
  return count;
}
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

/** SVG circular progress ring */
function Ring({ score, brand, size = 72 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : score > 0 ? '#ef4444' : '#d1d5db';
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth="5"
        className="text-gray-200 dark:text-gray-700" />
      <motion.circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={score > 0 ? color : '#d1d5db'} strokeWidth="5" strokeLinecap="round"
        strokeDasharray={circ}
        initial={{ strokeDashoffset: circ }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 0.9, delay: 0.3 }}
      />
    </svg>
  );
}

/** Section wrapper with sticky label */
function Section({ id, label, badge, children, bg = 'bg-gray-50 dark:bg-gray-950' }) {
  return (
    <section id={id} className={`py-14 px-4 md:px-10 ${bg}`}>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <span className="text-2xl font-bold font-display text-gray-900 dark:text-white">{label}</span>
          {badge && (
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
              {badge}
            </span>
          )}
        </div>
        {children}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STYLE 1 — Bento Grid + Glassmorphism
// ══════════════════════════════════════════════════════════════════════════════

function BentoPreview() {
  return (
    <div
      className="relative rounded-2xl overflow-hidden p-6"
      style={{
        background: 'linear-gradient(135deg, #0d9488 0%, #7c3aed 50%, #1e40af 100%)',
      }}
    >
      {/* Dot-grid overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
      }} />

      <div className="relative grid grid-cols-6 grid-rows-3 gap-3 h-[380px]">

        {/* Big tile — top performer */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}
          className="col-span-3 row-span-2 rounded-xl p-5 flex flex-col justify-between"
          style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.22)' }}
        >
          <div className="flex items-center justify-between">
            <span className="text-white/70 text-xs font-medium uppercase tracking-widest">Top Platform</span>
            <FaYoutube className="text-red-400 text-2xl" />
          </div>
          <div>
            <p className="text-5xl font-bold text-white mb-1">87</p>
            <p className="text-white/60 text-sm">Health Score</p>
            <div className="mt-3 h-1.5 rounded-full bg-white/20">
              <motion.div className="h-1.5 rounded-full bg-gradient-to-r from-green-400 to-teal-300"
                initial={{ width: 0 }} animate={{ width: '87%' }} transition={{ duration: 0.8, delay: 0.4 }} />
            </div>
          </div>
          <div className="flex gap-4 text-xs text-white/70">
            <span>2.41M views</span>
            <span className="text-green-300 flex items-center gap-0.5"><ArrowUpIcon className="w-3 h-3" />14%</span>
          </div>
        </motion.div>

        {/* Stat — views */}
        {[
          { label: 'Views',     value: '2.41M', color: 'from-sky-400 to-blue-500',   delay: 0.1 },
          { label: 'Revenue',   value: '$12.4K', color: 'from-amber-400 to-orange-500', delay: 0.15 },
          { label: 'Followers', value: '89.4K', color: 'from-pink-400 to-rose-500',  delay: 0.2 },
        ].map((s) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: s.delay }}
            className="col-span-3 rounded-xl p-4 flex flex-col justify-between"
            style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.22)' }}
          >
            <span className="text-white/60 text-xs">{s.label}</span>
            <p className={`text-2xl font-bold bg-gradient-to-r ${s.color} bg-clip-text text-transparent`}>
              {s.value}
            </p>
            <div className="flex items-center gap-1 text-green-300 text-xs">
              <ArrowUpIcon className="w-3 h-3" /> 8% vs last month
            </div>
          </motion.div>
        ))}

        {/* Platform strip */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="col-span-6 rounded-xl p-3 flex items-center gap-4"
          style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)' }}
        >
          <span className="text-white/50 text-xs mr-1">Platforms</span>
          {PLATFORMS.map(({ key, label, Icon, brand }) => (
            <div key={key} className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1.5 rounded-lg">
              <Icon style={{ color: brand }} className="text-sm" />
              <span className="text-white text-xs hidden md:inline">{label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STYLE 2 — Story Rings + Carousel
// ══════════════════════════════════════════════════════════════════════════════

function StoryRingsPreview() {
  const carouselRef = useRef(null);
  const posts = [
    { title: 'YouTube Vlog #42', views: '44.2K', likes: '2.1K', platform: PLATFORMS[0] },
    { title: 'TikTok Dance Trend', views: '31.8K', likes: '890', platform: PLATFORMS[1] },
    { title: 'Instagram Reel', views: '28.4K', likes: '1.4K', platform: PLATFORMS[2] },
    { title: 'Twitter Thread', views: '19.1K', likes: '440', platform: PLATFORMS[3] },
  ];

  return (
    <div className="bg-white dark:bg-dark-900 rounded-2xl border border-gray-200 dark:border-dark-700 p-6 space-y-6">
      {/* Story rings row */}
      <div>
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">
          Connected Platforms
        </p>
        <div className="flex gap-5 overflow-x-auto pb-1">
          {PLATFORMS.map(({ key, label, Icon, brand, score }) => {
            const connected = score > 0;
            return (
              <div key={key} className="flex flex-col items-center gap-2 flex-shrink-0">
                <div className="relative">
                  <Ring score={score} brand={brand} size={68} />
                  <div
                    className="absolute inset-0 flex items-center justify-center rounded-full"
                    style={{ padding: '10px' }}
                  >
                    <div
                      className="w-full h-full rounded-full flex items-center justify-center"
                      style={{ background: connected ? `${brand}18` : '#f3f4f6' }}
                    >
                      <Icon style={{ color: connected ? brand : '#9ca3af' }} className="text-lg" />
                    </div>
                  </div>
                </div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 text-center">{label}</span>
                <span className={`text-xs font-bold ${connected ? 'text-gray-800 dark:text-white' : 'text-gray-400'}`}>
                  {connected ? score : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Swipeable post carousel */}
      <div>
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
          Top Posts
        </p>
        <div ref={carouselRef} className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
          {posts.map((post, i) => {
            const { Icon, brand } = post.platform;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
                className="flex-shrink-0 snap-start w-44 rounded-xl border border-gray-100 dark:border-dark-700 p-3 bg-gray-50 dark:bg-dark-800"
              >
                {/* Thumbnail placeholder */}
                <div className="w-full h-24 rounded-lg mb-2 flex items-center justify-center"
                  style={{ background: `${brand}18` }}>
                  <Icon style={{ color: brand }} className="text-3xl" />
                </div>
                <p className="text-xs font-semibold text-gray-800 dark:text-white truncate mb-1">{post.title}</p>
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-0.5"><EyeIcon className="w-3 h-3" /> {post.views}</span>
                  <span className="flex items-center gap-0.5"><HeartIcon className="w-3 h-3 text-red-400" /> {post.likes}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
        <div className="flex gap-1.5 justify-center mt-2">
          {posts.map((_, i) => (
            <div key={i} className={`h-1.5 rounded-full transition-all ${i === 0 ? 'w-4 bg-primary-500' : 'w-1.5 bg-gray-300 dark:bg-gray-600'}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STYLE 3 — Dark Neon Creator Studio
// ══════════════════════════════════════════════════════════════════════════════

function DarkNeonPreview() {
  const connected = PLATFORMS.slice(0, 4);
  return (
    <div className="rounded-2xl p-6 space-y-5" style={{ background: '#0a0a0f' }}>
      {/* Platform neon cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {connected.map(({ key, label, Icon, brand, score }) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: connected.findIndex(p => p.key === key) * 0.07 }}
            className="rounded-xl p-4"
            style={{
              background: `${brand}0d`,
              border: `1px solid ${brand}40`,
              boxShadow: `0 0 18px ${brand}25, inset 0 0 12px ${brand}08`,
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <Icon style={{ color: brand }} className="text-xl" />
              <span className="text-xs font-bold" style={{ color: brand }}>
                {score}/100
              </span>
            </div>
            <p className="text-white text-sm font-semibold">{label}</p>
            <div className="mt-2 h-1 rounded-full bg-white/10">
              <motion.div
                className="h-1 rounded-full"
                style={{ background: `linear-gradient(90deg, ${brand}90, ${brand})` }}
                initial={{ width: 0 }} animate={{ width: `${score}%` }}
                transition={{ duration: 0.7, delay: 0.3 }}
              />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Live metrics panel */}
      <div className="rounded-xl p-4" style={{
        background: '#ffffff08',
        border: '1px solid #ffffff15',
      }}>
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-green-400 text-xs font-bold uppercase tracking-widest">Live Metrics</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="text-gray-500 text-xs mb-1">{s.label}</p>
              <p className="text-2xl font-bold"
                style={{ background: `linear-gradient(90deg, #fff, ${s.color})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {s.display}
              </p>
              <p className="text-green-400 text-xs flex items-center gap-0.5 mt-0.5">
                <ArrowUpIcon className="w-3 h-3" /> {s.change}%
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Glowing chart placeholder */}
      <div className="rounded-xl p-4 h-20 flex items-end gap-1" style={{ background: '#ffffff05', border: '1px solid #ffffff10' }}>
        {[35, 55, 42, 78, 62, 91, 74, 88, 65, 95, 82, 87].map((h, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-sm"
            style={{
              background: `linear-gradient(180deg, #0d9488, #14b8a640)`,
              boxShadow: '0 0 6px #0d948880',
            }}
            initial={{ height: 0 }}
            animate={{ height: `${h}%` }}
            transition={{ delay: i * 0.04, duration: 0.4 }}
          />
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STYLE 4 — Gradient Cards + Animated Counters
// ══════════════════════════════════════════════════════════════════════════════

function GradientCounterCard({ stat, active, delay }) {
  const raw = useCounter(stat.value, 1400, active);
  const formatted = raw >= 1_000_000
    ? `${(raw / 1_000_000).toFixed(1)}M`
    : raw >= 1_000
    ? `${(raw / 1_000).toFixed(1)}K`
    : raw.toString();
  const display = stat.label === 'Revenue' ? `$${formatted}` : formatted;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 overflow-hidden"
    >
      <div className="h-1" style={{ background: `linear-gradient(90deg, ${stat.color}, ${stat.color}80)` }} />
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="p-2 rounded-lg" style={{ background: `${stat.color}15` }}>
            <stat.Icon className="w-5 h-5" style={{ color: stat.color }} />
          </div>
          <span className="text-xs font-medium text-green-600 dark:text-green-400 flex items-center gap-0.5">
            <ArrowUpIcon className="w-3 h-3" />{stat.change}%
          </span>
        </div>
        <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{display}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stat.label}</p>
      </div>
    </motion.div>
  );
}

function GradientCardsPreview() {
  const [active, setActive] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setActive(true); },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="space-y-5">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {STATS.map((s, i) => (
          <GradientCounterCard key={s.label} stat={s} active={active} delay={i * 0.08} />
        ))}
      </div>

      {/* Platform engagement bars */}
      <div className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-500" />
        <div className="p-5">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Engagement Rate by Platform</p>
          <div className="space-y-3">
            {PLATFORMS.slice(0, 4).map(({ key, label, Icon, brand, score }, i) => (
              <div key={key} className="flex items-center gap-3">
                <Icon style={{ color: brand }} className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs text-gray-600 dark:text-gray-400 w-20 truncate">{label}</span>
                <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-dark-700">
                  <motion.div
                    className="h-2 rounded-full"
                    style={{ background: `linear-gradient(90deg, ${brand}90, ${brand})` }}
                    initial={{ width: 0 }}
                    animate={active ? { width: `${score}%` } : {}}
                    transition={{ duration: 0.7, delay: 0.2 + i * 0.08 }}
                  />
                </div>
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 w-8 text-right">{score > 0 ? `${score}%` : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scrolling live ticker */}
      <div className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 overflow-hidden">
        <div className="h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent" />
        <div className="px-4 py-2.5 overflow-hidden relative">
          <div className="flex gap-8 whitespace-nowrap" style={{ animation: 'ticker 14s linear infinite' }}>
            {[
              { label: '👁 Views',    val: '2.41M' },
              { label: '♥ Likes',    val: '89.4K' },
              { label: '💬 Comments', val: '12.8K' },
              { label: '💰 Revenue',  val: '$12.4K' },
              { label: '📈 Growth',  val: '+14%' },
              { label: '🔗 Links',   val: '6 platforms' },
            ].concat([
              { label: '👁 Views',    val: '2.41M' },
              { label: '♥ Likes',    val: '89.4K' },
              { label: '💬 Comments', val: '12.8K' },
              { label: '💰 Revenue',  val: '$12.4K' },
            ]).map((item, i) => (
              <span key={i} className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium text-gray-800 dark:text-gray-200">{item.label}</span>
                {' '}<span className="text-primary-600 dark:text-primary-400 font-bold">{item.val}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Preview Page
// ══════════════════════════════════════════════════════════════════════════════

export default function UIStylePreview() {
  const sections = [
    { id: 's1', label: 'Bento Grid + Glassmorphism' },
    { id: 's2', label: 'Story Rings + Carousel' },
    { id: 's3', label: 'Dark Neon Creator Studio' },
    { id: 's4', label: 'Gradient Cards + Live Counters' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-dark-950">
      {/* Sticky nav */}
      <div className="sticky top-0 z-50 bg-white/90 dark:bg-dark-900/90 backdrop-blur border-b border-gray-200 dark:border-dark-700 px-6 py-3 flex items-center gap-6 overflow-x-auto">
        <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">UI Style Preview</span>
        <div className="flex gap-2">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-700 dark:hover:text-primary-300 transition-colors whitespace-nowrap"
            >
              {s.label}
            </a>
          ))}
        </div>
      </div>

      {/* Option 1 */}
      <Section id="s1" label="Option 1 — Bento Grid + Glassmorphism" badge="Most modern SaaS" bg="bg-gray-100 dark:bg-dark-950">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-2xl">
          Asymmetric tile layout over a vivid gradient background. Cards use frosted-glass effect (backdrop-blur + semi-transparent). Works beautifully in both light and dark mode.
        </p>
        <BentoPreview />
      </Section>

      {/* Option 2 */}
      <Section id="s2" label="Option 2 — Story Rings + Swipeable Carousel" badge="Most social-native" bg="bg-white dark:bg-dark-900">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-2xl">
          Platform health shown as circular progress rings (inspired by Instagram Stories). Top content is a horizontal drag-to-scroll card carousel. Familiar to any social media user.
        </p>
        <StoryRingsPreview />
      </Section>

      {/* Option 3 */}
      <Section id="s3" label="Option 3 — Dark Neon Creator Studio" badge="Popular with streamers" bg="bg-gray-950">
        <p className="text-sm text-gray-400 mb-5 max-w-2xl">
          Deep near-black background, each platform card glows with its own brand color. Gradient text metrics. Popular with Twitch streamers, YouTubers, and gaming creators (Streamlabs / OBS aesthetic).
        </p>
        <DarkNeonPreview />
      </Section>

      {/* Option 4 */}
      <Section id="s4" label="Option 4 — Gradient Cards + Animated Counters" badge="Balanced & polished" bg="bg-gray-50 dark:bg-dark-950">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-2xl">
          Keeps the existing layout but elevates it — each card gets a gradient accent bar in its metric's color, numbers count up from zero on scroll-into-view, and a live ticker scrolls across the bottom. Closest to the current feel but visually much richer.
        </p>
        <GradientCardsPreview />
      </Section>
    </div>
  );
}
